import React, { useEffect } from 'react';
import { FaTimesCircle, FaClock, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

const CancellationPolicy = () => {
  useEffect(() => {
    document.title = "Cancellation Policy - SastaMastaRide";
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <FaTimesCircle className="text-red-600 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Cancellation Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-700">
          <div className="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-600">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> This website is operated by <strong>SastaMastaRide</strong>, managed by <strong>RKTECH Solutions</strong>.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Overview</h2>
            <p className="mb-4">
              This Cancellation Policy outlines the terms and conditions for cancelling ride bookings on the 
              SastaMastaRide platform. Both riders and passengers have the right to cancel bookings under 
              certain conditions, subject to the cancellation fees and timelines specified below.
            </p>
            <p>
              By booking a ride on our platform, you agree to this cancellation policy. Please read it carefully 
              before confirming your booking.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Cancellation by Passenger</h2>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Cancellation Timeline & Charges</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FaClock className="text-green-600" />
                    <h4 className="font-semibold text-gray-800">More than 24 hours before departure</h4>
                  </div>
                  <p className="text-gray-700">• <strong>Refund:</strong> 100% of ride fare (platform fee may apply)</p>
                  <p className="text-gray-700">• <strong>Cancellation Fee:</strong> None</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FaClock className="text-blue-600" />
                    <h4 className="font-semibold text-gray-800">12-24 hours before departure</h4>
                  </div>
                  <p className="text-gray-700">• <strong>Refund:</strong> 80% of ride fare</p>
                  <p className="text-gray-700">• <strong>Cancellation Fee:</strong> 20% of ride fare</p>
                </div>

                <div className="border-l-4 border-yellow-500 pl-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FaClock className="text-yellow-600" />
                    <h4 className="font-semibold text-gray-800">6-12 hours before departure</h4>
                  </div>
                  <p className="text-gray-700">• <strong>Refund:</strong> 50% of ride fare</p>
                  <p className="text-gray-700">• <strong>Cancellation Fee:</strong> 50% of ride fare</p>
                </div>

                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FaClock className="text-red-600" />
                    <h4 className="font-semibold text-gray-800">Less than 6 hours before departure or No-Show</h4>
                  </div>
                  <p className="text-gray-700">• <strong>Refund:</strong> No refund</p>
                  <p className="text-gray-700">• <strong>Cancellation Fee:</strong> 100% of ride fare</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <div className="flex items-start space-x-2">
                <FaExclamationTriangle className="text-yellow-600 text-xl mt-1" />
                <div>
                  <p className="text-sm text-gray-700">
                    <strong>Important:</strong> Platform service fee (typically 10-15%) is non-refundable in most cases, 
                    except when cancellation is due to platform error or rider no-show.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Cancellation by Rider</h2>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Rider Cancellation Policy</h3>
              <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                <li><strong>More than 24 hours before departure:</strong> No penalty, full refund to passengers</li>
                <li><strong>12-24 hours before departure:</strong> Rider may face rating impact, full refund to passengers</li>
                <li><strong>Less than 12 hours before departure:</strong> Rider may face penalties, full refund + compensation to passengers</li>
                <li><strong>No-Show:</strong> Severe penalties, full refund + compensation to passengers, possible account suspension</li>
              </ul>
            </div>

            <p className="text-gray-700 mb-4">
              Riders who cancel frequently or at short notice may face:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
              <li>Reduced visibility in search results</li>
              <li>Temporary suspension of account</li>
              <li>Permanent ban for repeated violations</li>
              <li>Financial penalties for last-minute cancellations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Cancellation Duration & Deadlines</h2>
            <div className="bg-blue-50 p-6 rounded-lg mb-4">
              <div className="flex items-start space-x-3">
                <FaClock className="text-blue-600 text-2xl mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Key Timeframes</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Free Cancellation Window:</strong> Up to 24 hours before scheduled departure time</li>
                    <li><strong>Partial Refund Window:</strong> 12-24 hours before departure (80% refund)</li>
                    <li><strong>Reduced Refund Window:</strong> 6-12 hours before departure (50% refund)</li>
                    <li><strong>No Refund Window:</strong> Less than 6 hours before departure</li>
                    <li><strong>Emergency Cancellation:</strong> Contact support immediately for special circumstances</li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> All times are calculated based on the scheduled departure time in the trip timezone. 
              Cancellation requests must be submitted through the platform or confirmed by our support team.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. How to Cancel a Booking</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">For Passengers:</h3>
              <ol className="list-decimal list-inside ml-4 space-y-2 text-gray-700">
                <li>Log in to your SastaMastaRide account</li>
                <li>Navigate to "My Bookings"</li>
                <li>Select the booking you want to cancel</li>
                <li>Click "Cancel Booking"</li>
                <li>Select the reason for cancellation</li>
                <li>Confirm the cancellation</li>
                <li>Review refund details (if applicable)</li>
              </ol>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">For Riders:</h3>
              <ol className="list-decimal list-inside ml-4 space-y-2 text-gray-700">
                <li>Log in to your SastaMastaRide account</li>
                <li>Go to "My Trips"</li>
                <li>Select the trip you want to cancel</li>
                <li>Click "Cancel Trip"</li>
                <li>Select the reason for cancellation</li>
                <li>Confirm cancellation and notify booked passengers</li>
                <li>Review any penalties or impacts</li>
              </ol>
            </div>

            <p className="text-gray-700 mt-4">
              <strong>Alternative:</strong> Contact our support team at{' '}
              <a href="mailto:support@sastamastaride.com" className="text-blue-600 hover:underline">
                support@sastamastaride.com
              </a>{' '}
              or call <a href="tel:+911800XXXXXX" className="text-blue-600 hover:underline">+91-1800-XXX-XXXX</a> for assistance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Special Circumstances</h2>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Full Refund (No Penalty) Applicable For:</h3>
                <ul className="list-disc list-inside ml-4 space-y-1 text-gray-700">
                  <li>Rider no-show at pickup location</li>
                  <li>Platform technical errors</li>
                  <li>Safety concerns reported and verified</li>
                  <li>Natural disasters or emergencies</li>
                  <li>Government restrictions or lockdowns</li>
                  <li>Vehicle breakdown (rider's vehicle)</li>
                  <li>Medical emergencies (with documentation)</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Refund Processing</h2>
            <p className="text-gray-700 mb-4">
              Refunds for cancelled bookings are processed as follows:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
              <li><strong>Processing Time:</strong> 3-5 business days after cancellation</li>
              <li><strong>Refund Method:</strong> Original payment method (preferred) or platform wallet</li>
              <li><strong>Bank Transfer:</strong> 7-14 business days for bank transfers</li>
              <li><strong>Notification:</strong> You will receive email confirmation once refund is processed</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              For detailed refund information, please refer to our{' '}
              <a href="/refund-policy" className="text-blue-600 hover:underline">Return & Refund Policy</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Contact Information</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">For cancellation-related queries:</p>
              <p className="text-gray-700"><strong>Email:</strong> support@sastamastaride.com</p>
              <p className="text-gray-700"><strong>Phone:</strong> +91-1800-XXX-XXXX</p>
              <p className="text-gray-700"><strong>Address:</strong> [Your Registered Address], India</p>
            </div>
          </section>

          <div className="bg-blue-50 p-6 rounded-lg mt-8">
            <div className="flex items-start space-x-3">
              <FaInfoCircle className="text-blue-600 text-2xl mt-1" />
              <div>
                <p className="text-gray-700">
                  <strong>Important:</strong> This cancellation policy is subject to change. We recommend reviewing it 
                  periodically. Continued use of our platform after policy changes constitutes acceptance of the new terms.
                </p>
                <p className="text-gray-700 mt-2">
                  For questions about this policy, contact us at{' '}
                  <a href="mailto:legal@sastamastaride.com" className="text-blue-600 hover:underline">
                    legal@sastamastaride.com
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;

